import { DateTime } from "luxon";
import type { PbjPrediction, PbjPredictionHoursMode, PbjPredictionManualHoursEntry } from "../../../utils/models";

// Order-sensitive REPORTED-HRD → adjusted-HPPD conversions for the Manage Manual Hours
// dialog. Pure and React-free so they mirror the backend recompute
// (`sum_prediction_manual_hours` / the greedy planner) exactly — the same parity-critical
// category as recMath. The dialog keeps only the summing useMemo that composes these.

// Count (weekday, weekend) days in [start, end] inclusive. Mirror of the BE's
// `_count_weekday_weekend_days` helper. Project standard uses Luxon's custom
// `weekdayISO` (Sun=0, Mon=1, ..., Sat=6) — weekend is Sun or Sat.
export const countWeekdayWeekendDays = (start: DateTime, end: DateTime): { weekday: number; weekend: number } => {
	if (!start.isValid || !end.isValid || end < start) return { weekday: 0, weekend: 0 };
	let weekday = 0;
	let weekend = 0;
	for (let cursor = start; cursor <= end; cursor = cursor.plus({ days: 1 })) {
		if (cursor.weekdayISO === 0 || cursor.weekdayISO === 6) weekend += 1;
		else weekday += 1;
	}
	return { weekday, weekend };
};

export const countWeekendDays = (startIso: string, endIso: string): number => {
	return countWeekdayWeekendDays(DateTime.fromISO(startIso).startOf("day"), DateTime.fromISO(endIso).startOf("day")).weekend;
};

/** Weekday / weekend day counts for an inclusive ISO date range; zeros for an invalid range. */
export const periodDayCounts = (startIso: string, endIso: string): { weekday: number; weekend: number } =>
	countWeekdayWeekendDays(DateTime.fromISO(startIso).startOf("day"), DateTime.fromISO(endIso).startOf("day"));

/**
 * Resolve one entered figure into the period TOTAL that gets stored.
 *
 * `total` passes the value through — the admin already typed the whole-period number.
 * `recurring` multiplies the per-day rate by that day class's day count.
 */
export const resolveTotalHours = (enteredValue: number, mode: PbjPredictionHoursMode, dayCount: number): number =>
	mode === "total" ? enteredValue : enteredValue * dayCount;

/** Recover the per-day rate an entry was typed at, for re-seeding the edit form. */
export const perDayRateForEntry = (entry: PbjPredictionManualHoursEntry): { weekday: number; weekend: number } => {
	const counts = periodDayCounts(entry.periodStartDate, entry.periodEndDate);
	return {
		weekday: counts.weekday > 0 ? entry.weekdayHours / counts.weekday : 0,
		weekend: counts.weekend > 0 ? entry.weekendHours / counts.weekend : 0,
	};
};

/** Trim a derived rate to something a number field can round-trip without float noise. */
export const formatHoursValue = (value: number): string => String(Number(value.toFixed(2)));

// The hours an entry contributes within a (startDate, effectiveEndDate) window,
// mirroring the BE's `sum_prediction_manual_hours` pro-rating: each day-class
// (weekday/weekend) is scaled by its own (overlap_days / entry_days) ratio. This
// is what makes the modal's projected delta MATCH the actual adj_HPPD bump the
// BE computes — without it, an entry that spans Apr-Jun viewed through a QTD
// window (Apr 1..today) would overstate its impact because the FE would divide
// the FULL hours by the (truncated) window denominator while the BE only added
// the in-window portion.
export const proRatedHoursForEntry = (
	entry: PbjPredictionManualHoursEntry,
	windowStart: DateTime,
	windowEnd: DateTime,
): { weekdayShare: number; weekendShare: number } => {
	const entryStart = DateTime.fromISO(entry.periodStartDate).startOf("day");
	const entryEnd = DateTime.fromISO(entry.periodEndDate).startOf("day");
	if (!entryStart.isValid || !entryEnd.isValid) return { weekdayShare: 0, weekendShare: 0 };
	const overlapStart = entryStart > windowStart ? entryStart : windowStart;
	const overlapEnd = entryEnd < windowEnd ? entryEnd : windowEnd;
	if (overlapEnd < overlapStart) return { weekdayShare: 0, weekendShare: 0 };
	const entryDays = countWeekdayWeekendDays(entryStart, entryEnd);
	const overlapDays = countWeekdayWeekendDays(overlapStart, overlapEnd);
	// Same defensive fallback as the BE: if an entry has no day of a given
	// class, divide-by-zero would make the share NaN — zero it out instead.
	const weekdayShare = entryDays.weekday > 0 ? entry.weekdayHours * (overlapDays.weekday / entryDays.weekday) : 0;
	const weekendShare = entryDays.weekend > 0 ? entry.weekendHours * (overlapDays.weekend / entryDays.weekend) : 0;
	return { weekdayShare, weekendShare };
};

// Resolve the effective window end the BE actually used. `dataThroughDate` is
// `min(end_date, today)` — when the quarter is fully in the future, the BE
// nulls it out and uses the raw end_date.
export const effectiveWindowEnd = (prediction: PbjPrediction, endDateIso: string): DateTime => {
	return DateTime.fromISO(prediction.dataThroughDate ?? endDateIso).startOf("day");
};

// Per-entry contribution in REPORTED-HRD space, pro-rated to the prediction's
// effective window. Callers multiply by the matching category's `scale` to
// convert to adjusted HPPD — the unit the "current" values are displayed in
// and the unit the greedy planner consumes.
export const reportedHrdDeltaForEntry = (
	entry: PbjPredictionManualHoursEntry,
	prediction: PbjPrediction,
	windowStart: DateTime,
	windowEnd: DateTime,
): number => {
	if (prediction.resolvedAverageCensus <= 0 || prediction.days <= 0) return 0;
	const { weekdayShare, weekendShare } = proRatedHoursForEntry(entry, windowStart, windowEnd);
	return (weekdayShare + weekendShare) / (prediction.resolvedAverageCensus * prediction.days);
};

// Per-entry contribution to the Weekend bucket in REPORTED-HRD space. Uses the
// weekend-only pro-rated share over the prediction's `weekendDays`.
export const reportedWeekendHrdDeltaForEntry = (
	entry: PbjPredictionManualHoursEntry,
	prediction: PbjPrediction,
	windowStart: DateTime,
	windowEnd: DateTime,
): number => {
	if (prediction.resolvedAverageCensus <= 0) return 0;
	const { weekendShare } = proRatedHoursForEntry(entry, windowStart, windowEnd);
	const weekendDays = prediction.weekendDays > 0 ? prediction.weekendDays : countWeekendDays(entry.periodStartDate, entry.periodEndDate);
	if (weekendDays <= 0) return 0;
	return weekendShare / (prediction.resolvedAverageCensus * weekendDays);
};

// `scale` on `PbjCategoryScore` = `nat_case_mix / facility_cm` — the same
// adjusted-per-reported multiplier the BE greedy planner uses. Default 1.0
// when the category is missing (defensive; the BE always emits all three).
export const scaleFor = (prediction: PbjPrediction, categoryId: string): number => {
	return prediction.categories.find((c) => c.id === categoryId)?.scale ?? 1;
};

// null = non-numeric (surfaced as an explicit error, blocks submit). A non-empty
// value is returned verbatim (range is validated separately, never clamped).
export const parseHours = (raw: string): number | null => {
	// DEFAULT: an empty hours field means "0 hours for this day-class" — the entry can
	// put all its hours in the other bucket. The BE still requires at least one bucket
	// > 0 (enforced by `hasPositive` in the dialog), so an all-empty form can't submit.
	if (raw.trim() === "") return 0;
	const num = Number(raw);
	if (!Number.isFinite(num)) return null;
	return num;
};
